import type {
  ModelCapabilities,
  ModelCapabilityName,
} from "@/shared/domain/orgs";

/** One flag's setting: follow the model family, or force it on or off. */
export type CapabilityState = "default" | "on" | "off";

/** Every capability flag, in the order the editor lists them, with its name. */
export const CAPABILITY_LABELS: Record<ModelCapabilityName, string> = {
  tool_use: "Tool use",
  prompt_caching: "Prompt caching",
  strict_alternation: "Strict user/assistant alternation",
  requires_tool_pairing: "Requires tool call/result pairing",
  toolset_must_cover_history: "Toolset must cover tools in history",
  tool_result_json_must_be_object: "Tool results must be JSON objects",
  rejects_empty_text: "Rejects empty text",
  can_disable_thinking: "Thinking can be turned off",
  thinks_by_default: "Thinks by default",
};

/** The flags in display order. */
export const CAPABILITY_FLAGS = Object.keys(
  CAPABILITY_LABELS,
) as ModelCapabilityName[];

/** How `flag` is set in `overrides`: unset reads as the family default. */
export function capabilityState(
  overrides: Partial<ModelCapabilities>,
  flag: ModelCapabilityName,
): CapabilityState {
  const value = overrides[flag];
  if (value === undefined) {
    return "default";
  }
  return value ? "on" : "off";
}

/**
 * A copy of `overrides` with `flag` forced on or off, or dropped (so it
 * follows the family) for `"default"`. Only set flags are ever sent.
 */
export function withCapabilityState(
  overrides: Partial<ModelCapabilities>,
  flag: ModelCapabilityName,
  state: CapabilityState,
): Partial<ModelCapabilities> {
  const next = { ...overrides };
  if (state === "default") {
    delete next[flag];
  } else {
    next[flag] = state === "on";
  }
  return next;
}

/**
 * The label of a flag's "Default" option, naming what the default is when
 * that is known. The effective value is the family default only while the
 * flag is not overridden; once it is, the default is not known here.
 */
export function defaultOptionLabel({
  flag,
  overrides,
  effective,
  family,
}: {
  flag: ModelCapabilityName;
  overrides: Partial<ModelCapabilities>;
  /** Effective capabilities, when the server reported them. */
  effective: ModelCapabilities | undefined;
  family: string | null;
}): string {
  if (effective === undefined) {
    return "Default (by model family)";
  }
  if (overrides[flag] !== undefined) {
    return family === null
      ? "Default (family setting)"
      : `Default (${family}'s setting)`;
  }
  const value = effective[flag] ? "on" : "off";
  return family === null
    ? `Default (${value})`
    : `Default (${value} for ${family})`;
}
